import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { CreditCard, ExternalLink, Gift, Copy, Users } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [openaiKey, setOpenaiKey] = useState("");
  const [anthropicKey, setAnthropicKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    api.get("/referral/me").then((r) => setReferral(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (openaiKey) payload.openai_key = openaiKey;
      if (anthropicKey) payload.anthropic_key = anthropicKey;
      await api.post("/settings/api-keys", payload);
      toast.success("Saved. Your AI generations will now use your key.");
      setOpenaiKey(""); setAnthropicKey("");
      refresh();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post("/payments/portal", { return_url: window.location.origin + "/settings" });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Portal unavailable");
      setPortalLoading(false);
    }
  };

  const isPro = user?.plan === "pro_monthly" || user?.plan === "pro_yearly";
  const hasStripeCustomer = !!user?.stripe_customer_id;

  return (
    <AppShell>
      <div className="mb-8">
        <div className="label-cap mb-2">// Account</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Settings</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
        <div className="card-brutal">
          <div className="font-display font-bold text-lg mb-4">Profile</div>
          <div className="space-y-2 text-sm">
            <div><span className="label-cap">Name</span><div>{user?.name}</div></div>
            <div><span className="label-cap">Email</span><div className="font-mono">{user?.email}</div></div>
            <div><span className="label-cap">Role</span><div>{user?.role}</div></div>
            <div><span className="label-cap">Plan</span><div>{user?.plan}</div></div>
          </div>
        </div>

        <div className="card-brutal">
          <div className="font-display font-bold text-lg mb-2 flex items-center gap-2"><CreditCard size={18} /> Subscription</div>
          {user?.role === "admin" ? (
            <p className="text-sm text-zinc-600">Admin accounts have unlimited access. No billing.</p>
          ) : isPro ? (
            <>
              <p className="text-sm text-zinc-600 mb-4">Manage your billing, payment method, invoices, or cancel anytime via Stripe.</p>
              <button onClick={openPortal} disabled={portalLoading || !hasStripeCustomer} className="btn-brutal w-full" data-testid="stripe-portal-btn">
                <ExternalLink size={16} /> {portalLoading ? "Opening…" : "Open Customer Portal"}
              </button>
              {!hasStripeCustomer && <p className="text-xs text-zinc-500 mt-2">Customer ID is being synced. Refresh in a minute.</p>}
            </>
          ) : (
            <>
              <p className="text-sm text-zinc-600 mb-4">You're on the Free plan (5 lead magnets max). Unlock unlimited with Pro.</p>
              <a href="/pricing" className="btn-brutal w-full" data-testid="upgrade-from-settings">Lock In $89/Year</a>
            </>
          )}
        </div>

        <div className="card-brutal lg:col-span-2 border-[#FF4F00]" data-testid="referral-card">
          <div className="flex items-center gap-2 mb-2">
            <Gift size={18} className="text-[#FF4F00]" />
            <div className="font-display font-bold text-lg">Refer & Earn — 30% recurring</div>
          </div>
          <p className="text-sm text-zinc-600 mb-4">
            Share VibeLead with marketers in your network. You earn <strong>30% commission</strong> on every Pro subscription they buy — for as long as they stay subscribed.
          </p>
          {referral ? (
            <>
              <div className="grid grid-cols-3 gap-0 border-2 border-black mb-4">
                <RefStat icon={Users} label="Referred" value={referral.total_referred} testid="ref-stat-referred" />
                <RefStat icon={Gift} label="Paid" value={referral.paid_referrals} testid="ref-stat-paid" />
                <RefStat icon={CreditCard} label="Earned" value={`$${referral.balance_usd.toFixed(2)}`} highlight testid="ref-stat-earned" />
              </div>
              <label className="label-cap block mb-2">Your referral link</label>
              <div className="flex gap-2">
                <input readOnly value={`${window.location.origin}/?ref=${referral.code}`}
                  className="input-brutal flex-1 font-mono text-xs" data-testid="referral-link-input" />
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/?ref=${referral.code}`); toast.success("Copied"); }}
                  className="btn-brutal !px-4" data-testid="copy-referral-btn"><Copy size={14} /></button>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                Anyone who signs up via this link is permanently linked to you. {referral.balance_usd > 0 && "Contact support to redeem your earnings as a Pro discount or cash payout."}
              </p>
            </>
          ) : (
            <p className="text-sm text-zinc-500">Loading referral info…</p>
          )}
        </div>

        <div className="card-brutal lg:col-span-2">
          <div className="font-display font-bold text-lg mb-2">Bring Your Own AI Key</div>
          <p className="text-sm text-zinc-600 mb-4">VibeLead generates copy with our universal LLM by default. Optionally add your own key — it'll be used instead, with no platform credit deducted.</p>
          <div className="space-y-4">
            <div>
              <label className="label-cap block mb-2">OpenAI API Key</label>
              <input type="password" value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)} className="input-brutal" placeholder={user?.openai_key ? "•••••• (set)" : "sk-..."} data-testid="openai-key-input" />
            </div>
            <div>
              <label className="label-cap block mb-2">Anthropic API Key</label>
              <input type="password" value={anthropicKey} onChange={(e) => setAnthropicKey(e.target.value)} className="input-brutal" placeholder={user?.anthropic_key ? "•••••• (set)" : "sk-ant-..."} data-testid="anthropic-key-input" />
            </div>
            <button onClick={save} disabled={saving} className="btn-brutal w-full" data-testid="save-keys-btn">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function RefStat({ icon: Icon, label, value, highlight, testid }) {
  return (
    <div className={`p-4 border-r-2 last:border-r-0 border-black ${highlight ? "bg-[#FF4F00] text-white" : "bg-white"}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase opacity-80 mb-2"><Icon size={12} /> {label}</div>
      <div className="font-display font-black text-2xl">{value}</div>
    </div>
  );
}
